import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { z } from "zod";
import { getProjectPortal } from "@/lib/portal.functions";
import type { PortalProposal } from "@/lib/portal-shared";
import { Layout } from "@/components/site/Layout";

export const Route = createFileRoute("/clients/$client/projects/$project/")({
  validateSearch: z.object({ t: z.string().optional() }),
  loaderDeps: ({ search }) => ({ t: search.t ?? "" }),
  loader: async ({ params, deps }) => {
    if (!deps.t) throw notFound();
    const res = await getProjectPortal({
      data: { client: params.client, project: params.project, token: deps.t },
    });
    if (!res.ok || !res.project || !res.client) throw notFound();
    return { client: res.client, project: res.project, proposals: res.proposals ?? [] };
  },
  head: () => ({
    meta: [
      { title: "Project proposals — PULSO33" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Private proposal documents shared by PULSO33." },
    ],
  }),
  component: ProjectProposalsPage,
});

function formatSize(bytes: number | null) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ProjectProposalsPage() {
  const data = Route.useLoaderData();
  const { t } = Route.useSearch();
  const params = Route.useParams();

  return (
    <Layout>
      <section className="mx-auto w-full max-w-4xl px-4 py-16 md:py-24">
        <Link
          to="/clients/$client/projects"
          params={{ client: params.client }}
          search={{ t }}
          className="font-mono text-xs uppercase tracking-[0.2em] text-primary hover:underline"
        >
          ← {data.client.name}
        </Link>
        <h1 className="mt-3 font-display text-3xl md:text-4xl text-foreground">
          {data.project.name}
        </h1>
        {data.project.description ? (
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            {data.project.description}
          </p>
        ) : null}

        <div className="mt-10 grid gap-3">
          {data.proposals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No proposals published yet.</p>
          ) : (
            data.proposals.map((p: PortalProposal) => (
              <a
                key={p.id}
                href={`/clients/${params.client}/projects/${params.project}/${p.slug}?t=${encodeURIComponent(t ?? "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
              >
                <span>
                  <span className="block font-display text-lg text-foreground group-hover:text-primary">
                    {p.title}
                  </span>
                  <span className="mt-1 block font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    {p.kind === "html" ? "Web proposal" : "Document"} {formatSize(p.file_size)}
                  </span>
                </span>
                <span className="text-primary">→</span>
              </a>
            ))
          )}
        </div>
      </section>
    </Layout>
  );
}
